import React from "react";
import { Link } from "react-router-dom";
import {
  formatScore,
  horseName,
  levelLabel,
  metricDefinitions,
  score,
} from "./reportMetrics";

export default function HorseComparison({
  reports,
  onRemove,
  onClear,
  reportReturnState,
}) {
  if (!reports.length) return null;
  const definitions = metricDefinitions(reports[0].ml_model);
  const best = (getter) =>
    Math.max(...reports.map((report) => getter(report) ?? -Infinity));
  function cells(getter, formatter, highlight = true) {
    return reports.map((report) => {
      const value = getter(report);
      return (
        <td
          key={report.id}
          className={
            highlight &&
            value != null &&
            reports.length > 1 &&
            value === best(getter)
              ? "is-best"
              : ""
          }
        >
          {formatter(value)}
        </td>
      );
    });
  }
  return (
    <section
      className="report-panel report-comparison"
      id="compare"
      aria-labelledby="compare-title"
    >
      <div className="report-section-heading">
        <div>
          <p className="report-eyebrow">Make a confident shortlist</p>
          <h2 id="compare-title">
            Compare horses <small>{reports.length} / 4</small>
          </h2>
        </div>
        <button onClick={onClear}>Clear comparison</button>
      </div>
      <p className="report-note">
        {reports.length < 2
          ? "Choose another horse from the table to compare its pedigree potential."
          : "The strongest available value in each row is highlighted. Scores and model levels are compared within the same model."}
      </p>
      <p className="report-note">Scroll horizontally to see every horse.</p>
      <div
        className="report-table-scroll"
        tabIndex="0"
        aria-label="Horse comparison, scroll horizontally to see every horse"
      >
        <table>
          <thead>
            <tr>
              <th scope="col">Pedigree insight</th>
              {reports.map((report) => (
                <th scope="col" key={report.id}>
                  <span className="report-chip">
                    {report.lot ? `Lot ${report.lot}` : `Report ${report.id}`}
                  </span>
                  <Link to={report.web_url} state={reportReturnState}>
                    {horseName(report)}
                  </Link>
                  <button
                    aria-label={`Remove ${horseName(report)} from comparison`}
                    onClick={() => onRemove(report.id)}
                  >
                    Remove
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Pedigree score</th>
              {cells(score, (value) => (
                <strong className="report-comparison__score">
                  {formatScore(value)}
                </strong>
              ))}
            </tr>
            <tr>
              <th scope="row">Difference from first horse</th>
              {cells(
                (report) =>
                  score(report) != null && score(reports[0]) != null
                    ? score(report) - score(reports[0])
                    : null,
                (value) =>
                  value == null
                    ? "—"
                    : `${value > 0 ? "+" : ""}${value.toFixed(1)}`,
                false,
              )}
            </tr>
            <tr>
              <th scope="row">Population tier</th>
              {cells(
                (report) => report.top_value,
                (value) => (value == null ? "—" : `Top ${value}%`),
                false,
              )}
            </tr>
            {definitions.map(({ key, label }) => (
              <tr key={key}>
                <th scope="row">{label}</th>
                {cells(
                  (report) => report[key],
                  (value) => (
                    <>
                      <span>{levelLabel(value)}</span>
                      <div className="report-comparison__bar">
                        {value != null && (
                          <span style={{ width: `${(value / 6) * 100}%` }} />
                        )}
                      </div>
                    </>
                  ),
                )}
              </tr>
            ))}
            <tr>
              <th scope="row">Top pick</th>
              {cells(
                (report) => report.is_top_pick,
                (value) => (value ? "Yes" : "—"),
                false,
              )}
            </tr>
            {Number(reports[0].ml_model) === 1 &&
              [
                ["Precocity", "bool_pre"],
                ["Soundness", "bool_sou"],
              ].map(([label, key]) => (
                <tr key={key}>
                  <th scope="row">{label}</th>
                  {cells(
                    (report) => report[key],
                    (value) =>
                      value == null
                        ? "Unavailable"
                        : value
                          ? "High potential"
                          : "Not flagged as high",
                    false,
                  )}
                </tr>
              ))}
            <tr>
              <th scope="row">Sire country</th>
              {cells(
                (report) => report.sire?.country,
                (value) => value || "—",
                false,
              )}
            </tr>
            <tr>
              <th scope="row">Dam country</th>
              {cells(
                (report) => report.dam?.country,
                (value) => value || "—",
                false,
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
